/**
 * Below-fold drill-down table — Fork G / Dashboard v2.
 *
 * Fetches `/api/dashboard/:eventId/drilldown` with ?page&pageSize&sort&direction&channel.
 * Sortable by spend, duration, first_scan, scans (backend allowlist — any
 * other value silently falls back to first_scan DESC).
 * Channel filter: preorder | walkup | kiosk | cashier | mobile | online.
 * CSV export respects the current filter by streaming the active page set
 * client-side — no secondary endpoint, no PII leaves the backend the table
 * isn't already showing.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, Download, Filter } from 'lucide-react'

type DrillSort = 'spend' | 'duration' | 'first_scan' | 'scans'
type DrillDirection = 'asc' | 'desc'

interface DrillRow {
  band_id: number
  band_barcode: string | null
  plan_name: string
  purchase_channel: string | null
  scans: number
  duration_seconds: number
  total_spent: number
  first_scan_utc: string | null
  last_scan_utc: string | null
}

interface DrillEnvelope {
  source: 'pg'
  event_id: number
  page: number
  page_size: number
  total: number
  sort: DrillSort
  direction: DrillDirection
  channel: string | null
  rows: DrillRow[]
  generated_at: string
}

const DEFAULT_PAGE_SIZE = 25

// Channel categories must match the backend CHANNEL_MAP keys in
// apps/backend/src/dashboard/pg-dashboard-extras.js. The backend maps each
// to the raw ETL values (kiosk_walkup, cashier_walkup, online_preorder,
// online_eticket) produced by butler_db_etl.py.
const CHANNEL_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All channels' },
  { value: 'preorder', label: 'Preorder' },
  { value: 'kiosk', label: 'Kiosk' },
  { value: 'cashier', label: 'Cashier' },
  { value: 'online', label: 'Online' },
  { value: 'walkup', label: 'Walkup' },
]

const fmtUSD = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)

const fmtDuration = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins < 60) return `${mins}:${String(secs).padStart(2, '0')}`
  const hours = Math.floor(mins / 60)
  const remainingMins = mins % 60
  return `${hours}h ${String(remainingMins).padStart(2, '0')}m`
}

const fmtFirstScan = (iso: string | null) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

function csvEscape(value: string | number | null): string {
  if (value === null || value === undefined) return ''
  const str = String(value)
  // OWASP: neutralize formula injection for operator-controlled exports
  if (/^[=+\-@\t\r]/.test(str)) {
    return `"'${str.replace(/"/g, '""')}"`
  }
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

function buildCsv(rows: DrillRow[]): string {
  const header = [
    'band_id',
    'band_barcode',
    'plan_name',
    'purchase_channel',
    'scans',
    'duration_seconds',
    'total_spent_usd',
    'first_scan_utc',
    'last_scan_utc',
  ]
  const lines = [header.join(',')]
  for (const r of rows) {
    lines.push(
      [
        r.band_id,
        r.band_barcode,
        r.plan_name,
        r.purchase_channel,
        r.scans,
        r.duration_seconds,
        r.total_spent,
        r.first_scan_utc,
        r.last_scan_utc,
      ]
        .map(csvEscape)
        .join(','),
    )
  }
  return lines.join('\n')
}

export interface DashboardDrillDownProps {
  eventId: number
  authToken?: string | null
  pageSize?: number
}

export function DashboardDrillDown({
  eventId,
  authToken,
  pageSize = DEFAULT_PAGE_SIZE,
}: DashboardDrillDownProps) {
  const [envelope, setEnvelope] = useState<DrillEnvelope | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [sort, setSort] = useState<DrillSort>('first_scan')
  const [direction, setDirection] = useState<DrillDirection>('desc')
  const [channel, setChannel] = useState<string>('')
  const linkRef = useRef<HTMLAnchorElement | null>(null)

  // Reset page when filters change so the user isn't stranded on page 42
  // of a filtered view that only has 3 pages.
  useEffect(() => {
    setPage(0)
  }, [eventId, sort, direction, channel, pageSize])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    params.set('sort', sort)
    params.set('direction', direction)
    if (channel) params.set('channel', channel)

    const headers: Record<string, string> = {}
    if (authToken) headers.Authorization = `Bearer ${authToken}`

    fetch(`/api/dashboard/${eventId}/drilldown?${params.toString()}`, {
      headers,
      credentials: 'same-origin',
    })
      .then(async (res) => {
        if (res.status === 503) {
          return null
        }
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        const body = (await res.json()) as Partial<DrillEnvelope>
        // Defensive shape check — if the envelope is missing the required
        // `rows` array (stale cache, routing mismatch, dev fixture), treat
        // it as unavailable rather than crashing on a `.length` deref.
        if (!body || !Array.isArray(body.rows)) {
          return null
        }
        return body as DrillEnvelope
      })
      .then((data) => {
        if (cancelled) return
        setEnvelope(data)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'failed to load drill-down')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [eventId, authToken, page, pageSize, sort, direction, channel])

  const handleSort = useCallback(
    (key: DrillSort) => {
      if (sort === key) {
        setDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
      } else {
        setSort(key)
        setDirection('desc')
      }
    },
    [sort],
  )

  const handleExport = useCallback(() => {
    if (!envelope || envelope.rows.length === 0) return
    const csv = buildCsv(envelope.rows)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const fileName = `butler-drilldown-event-${envelope.event_id}-p${envelope.page + 1}.csv`
    const link = linkRef.current
    if (link) {
      link.href = url
      link.download = fileName
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 2000)
    }
  }, [envelope])

  const totalPages = useMemo(() => {
    if (!envelope || envelope.total === 0) return 1
    return Math.max(1, Math.ceil(envelope.total / envelope.page_size))
  }, [envelope])

  return (
    <section
      data-testid="dashboard-drilldown"
      className="mt-6 overflow-hidden rounded-card border border-hairline bg-white shadow-card-soft dark:border-slate-700 dark:bg-slate-800"
    >
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline px-4 py-3 dark:border-slate-700">
        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-butler-700 dark:text-butler-200">
            Band Session Log
          </h3>
          <p className="text-xs text-ink-soft dark:text-slate-400">
            {envelope
              ? `Showing ${envelope.rows.length} of ${envelope.total.toLocaleString()} bands`
              : '\u00A0'}
          </p>
          {/* Codex pass 2 (2026-04-18): ETL currently leaves
              band_session_rollup.purchase_channel NULL until the enrichment
              pass runs. If every visible row is NULL, channel filter results
              will be empty — hint instead of silently misleading operators. */}
          {envelope && envelope.rows.length > 0 &&
            envelope.rows.every((r) => !r.purchase_channel) ? (
            <p className="mt-0.5 text-[10px] text-glow-700 dark:text-glow-300">
              Channel enrichment pending — filter may return empty results.
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1 text-[11px] text-ink-soft dark:text-slate-400">
            <Filter className="h-3 w-3" aria-hidden="true" />
            Channel
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              className="rounded-md border border-hairline bg-white px-2 py-1 text-xs text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-butler-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
            >
              {CHANNEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={handleExport}
            disabled={!envelope || envelope.rows.length === 0}
            className="inline-flex items-center gap-1 rounded-md border border-butler-300 bg-butler-50 px-2 py-1 text-xs font-medium text-butler-700 transition hover:bg-butler-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-butler-700 dark:bg-butler-900/30 dark:text-butler-200"
          >
            <Download className="h-3 w-3" aria-hidden="true" />
            Export CSV
          </button>
          <a ref={linkRef} hidden />
        </div>
      </header>

      <div className="relative overflow-x-auto">
        {loading ? (
          <div className="p-6 text-center text-xs text-ink-soft dark:text-slate-400">
            Loading band session log…
          </div>
        ) : error ? (
          <div className="p-6 text-center text-xs text-marquee-700 dark:text-marquee-300">
            Couldn't load drill-down: {error}
          </div>
        ) : !envelope ? (
          <div className="p-6 text-center text-xs text-ink-soft dark:text-slate-400">
            Drill-down data unavailable (server PG path is offline).
          </div>
        ) : envelope.rows.length === 0 ? (
          <div className="p-6 text-center text-xs text-ink-soft dark:text-slate-400">
            No bands match this filter yet.
          </div>
        ) : (
          <table className="w-full min-w-[640px] text-left text-xs">
            <thead className="border-b border-hairline bg-butler-50/40 dark:border-slate-700 dark:bg-slate-900/30">
              <tr className="text-[10px] uppercase tracking-wider text-ink-soft dark:text-slate-400">
                <th scope="col" className="px-4 py-2">Band</th>
                <th scope="col" className="px-3 py-2">Plan</th>
                <th scope="col" className="px-3 py-2">Channel</th>
                <SortableTh
                  scope="col"
                  className="px-3 py-2"
                  active={sort === 'scans'}
                  direction={direction}
                  onClick={() => handleSort('scans')}
                >
                  Scans
                </SortableTh>
                <SortableTh
                  scope="col"
                  className="px-3 py-2"
                  active={sort === 'duration'}
                  direction={direction}
                  onClick={() => handleSort('duration')}
                >
                  Duration
                </SortableTh>
                <SortableTh
                  scope="col"
                  className="px-3 py-2"
                  active={sort === 'spend'}
                  direction={direction}
                  onClick={() => handleSort('spend')}
                >
                  Spend
                </SortableTh>
                <SortableTh
                  scope="col"
                  className="px-3 py-2"
                  active={sort === 'first_scan'}
                  direction={direction}
                  onClick={() => handleSort('first_scan')}
                >
                  First scan
                </SortableTh>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline text-ink dark:divide-slate-700 dark:text-slate-100">
              {envelope.rows.map((row) => (
                <tr
                  key={`${row.band_id}-${row.first_scan_utc ?? ''}`}
                  className="transition-colors hover:bg-butler-50/50 dark:hover:bg-slate-700/40"
                >
                  <td className="px-4 py-2 font-mono text-[11px]">
                    {row.band_barcode || `#${row.band_id}`}
                  </td>
                  <td className="px-3 py-2">{row.plan_name}</td>
                  <td className="px-3 py-2 capitalize">{row.purchase_channel ?? '—'}</td>
                  <td className="px-3 py-2 font-mono">{row.scans.toLocaleString()}</td>
                  <td className="px-3 py-2 font-mono">{fmtDuration(row.duration_seconds)}</td>
                  <td className="px-3 py-2 font-mono text-butler-700 dark:text-butler-200">
                    {fmtUSD(row.total_spent)}
                  </td>
                  <td className="px-3 py-2 text-ink-soft dark:text-slate-400">
                    {fmtFirstScan(row.first_scan_utc)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {envelope ? (
        <footer className="flex items-center justify-between border-t border-hairline bg-surface-stripe px-4 py-2 text-xs text-ink-soft dark:border-slate-700 dark:bg-slate-900/30 dark:text-slate-400">
          <span>
            Page {envelope.page + 1} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={envelope.page === 0}
              className="rounded-md border border-hairline px-2 py-0.5 text-xs font-medium text-ink transition hover:bg-butler-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={envelope.page + 1 >= totalPages}
              className="rounded-md border border-hairline px-2 py-0.5 text-xs font-medium text-ink transition hover:bg-butler-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Next
            </button>
          </div>
        </footer>
      ) : null}
    </section>
  )
}

function SortableTh({
  children,
  active,
  direction,
  onClick,
  ...rest
}: React.ThHTMLAttributes<HTMLTableCellElement> & {
  active: boolean
  direction: DrillDirection
  onClick: () => void
}) {
  return (
    <th {...rest}>
      <button
        type="button"
        onClick={onClick}
        aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider transition-colors hover:text-butler-700 dark:hover:text-butler-200 ${
          active ? 'text-butler-700 dark:text-butler-200' : 'text-ink-soft dark:text-slate-400'
        }`}
      >
        {children}
        {active ? (
          direction === 'asc' ? (
            <ArrowUp className="h-3 w-3" aria-hidden="true" />
          ) : (
            <ArrowDown className="h-3 w-3" aria-hidden="true" />
          )
        ) : null}
      </button>
    </th>
  )
}
